import React, { useEffect, useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Gauge, Gift, ShieldAlert, Settings2, DatabaseZap, Upload, Plus, Trash2, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const nf = (n) => (Number(n) || 0).toLocaleString("id-ID");

export default function PointsAdminPanel() {
  return (
    <div data-testid="points-admin-panel">
      <div className="mb-4">
        <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><Gauge className="w-5 h-5 text-showty-red" /> Point & Tier System</h3>
        <p className="text-sm text-muted-foreground">Kelola performa, reward, penyesuaian, konfigurasi aturan, dan backfill. Semua kalkulasi berjalan di server dengan ledger anti-double-count.</p>
      </div>
      <Tabs defaultValue="performance">
        <TabsList className="bg-showty-secondary flex-wrap h-auto">
          <TabsTrigger value="performance" data-testid="pa-tab-performance"><Gauge className="w-4 h-4 mr-1.5" /> Performa</TabsTrigger>
          <TabsTrigger value="rewards" data-testid="pa-tab-rewards"><Gift className="w-4 h-4 mr-1.5" /> Reward Store</TabsTrigger>
          <TabsTrigger value="audit" data-testid="pa-tab-audit"><ShieldAlert className="w-4 h-4 mr-1.5" /> Penyesuaian & Audit</TabsTrigger>
          <TabsTrigger value="config" data-testid="pa-tab-config"><Settings2 className="w-4 h-4 mr-1.5" /> Konfigurasi</TabsTrigger>
          <TabsTrigger value="backfill" data-testid="pa-tab-backfill"><DatabaseZap className="w-4 h-4 mr-1.5" /> Backfill</TabsTrigger>
        </TabsList>
        <TabsContent value="performance" className="mt-5"><PerformanceTab /></TabsContent>
        <TabsContent value="rewards" className="mt-5"><RewardsTab /></TabsContent>
        <TabsContent value="audit" className="mt-5"><AuditTab /></TabsContent>
        <TabsContent value="config" className="mt-5"><ConfigTab /></TabsContent>
        <TabsContent value="backfill" className="mt-5"><BackfillTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Performance ---------------- */
const PerformanceTab = () => {
  const [members, setMembers] = useState([]);
  const [rows, setRows] = useState([]);
  const empty = { user_id: "", path: "kol", cohort: "", verified: true, evidence: "", gmv_net: 0, orders_valid: 0, clicks: 0, live_count: 0, events_valid: 0, campaigns_done: 0, reviews_new: 0, referrals_active: 0 };
  const [f, setF] = useState(empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    const [m, r] = await Promise.all([api.get("/points/members"), api.get("/points/performance")]);
    setMembers(m.data); setRows(r.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const save = async () => {
    if (!f.user_id) return toast.error("Pilih member");
    try {
      await api.post("/points/performance", {
        ...f, gmv_net: Number(f.gmv_net) || 0, orders_valid: Number(f.orders_valid) || 0, clicks: Number(f.clicks) || 0,
        live_count: Number(f.live_count) || 0, events_valid: Number(f.events_valid) || 0, campaigns_done: Number(f.campaigns_done) || 0,
        reviews_new: Number(f.reviews_new) || 0, referrals_active: Number(f.referrals_active) || 0,
      });
      toast.success("Data performa disimpan & point direkonsiliasi");
      setF(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("verified", "true");
    try { const r = await api.post("/points/performance/import", fd); toast.success(`Import ${r.data.imported} baris`); load(); }
    catch (err) { toast.error("Gagal import CSV"); }
    e.target.value = "";
  };

  const isAff = f.path === "aff";
  return (
    <div>
      <div className="rounded-xl bg-showty-cream border border-showty-gold/30 px-4 py-2.5 mb-4 text-xs text-showty-navy" data-testid="perf-notice">
        ⚠️ Data views/order/GMV/CR <b>belum tersambung otomatis</b> ke TikTok/Shopee. Input manual atau impor CSV di bawah, dengan status verifikasi. Point hanya diterbitkan saat <b>Verified</b>.
      </div>
      <div className="rounded-2xl border border-border bg-white p-5 mb-5" data-testid="perf-form">
        <div className="flex items-center justify-between mb-3">
          <p className="font-medium text-showty-navy">Input / Update Performa (per member · per bulan)</p>
          <label className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 cursor-pointer hover:bg-showty-cream" data-testid="perf-import-label">
            <Upload className="w-4 h-4" /> Impor CSV<input type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="perf-import" />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><Label className="text-xs">Member</Label>
            <Select value={f.user_id} onValueChange={(v) => { const m = members.find((x) => x.user_id === v); set("user_id", v); set("path", m?.role === "affiliator" ? "aff" : "kol"); }}>
              <SelectTrigger className="mt-1 h-9" data-testid="perf-member"><SelectValue placeholder="Pilih member" /></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email} ({m.role === "affiliator" ? "Aff" : "KOL"})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Jalur</Label>
            <Select value={f.path} onValueChange={(v) => set("path", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="perf-path"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="kol">KOL</SelectItem><SelectItem value="aff">Affiliator</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Cohort (YYYY-MM)</Label><Input className="mt-1 h-9" placeholder="kosong = bulan ini" value={f.cohort} onChange={(e) => set("cohort", e.target.value)} data-testid="perf-cohort" /></div>
          <div><Label className="text-xs">Status</Label>
            <Select value={f.verified ? "1" : "0"} onValueChange={(v) => set("verified", v === "1")}>
              <SelectTrigger className="mt-1 h-9" data-testid="perf-verified"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="1">Verified (terbitkan point)</SelectItem><SelectItem value="0">Belum verified (nol-kan)</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">GMV net (Rp)</Label><Input type="number" className="mt-1 h-9" value={f.gmv_net} onChange={(e) => set("gmv_net", e.target.value)} data-testid="perf-gmv" /></div>
          <div><Label className="text-xs">Order valid</Label><Input type="number" className="mt-1 h-9" value={f.orders_valid} onChange={(e) => set("orders_valid", e.target.value)} data-testid="perf-orders" /></div>
          {isAff ? (<>
            <div><Label className="text-xs">Klik unik valid</Label><Input type="number" className="mt-1 h-9" value={f.clicks} onChange={(e) => set("clicks", e.target.value)} data-testid="perf-clicks" /></div>
            <div><Label className="text-xs">Live eligible</Label><Input type="number" className="mt-1 h-9" value={f.live_count} onChange={(e) => set("live_count", e.target.value)} data-testid="perf-live" /></div>
          </>) : (<>
            <div><Label className="text-xs">Event tervalidasi</Label><Input type="number" className="mt-1 h-9" value={f.events_valid} onChange={(e) => set("events_valid", e.target.value)} data-testid="perf-events" /></div>
            <div><Label className="text-xs">Campaign selesai</Label><Input type="number" className="mt-1 h-9" value={f.campaigns_done} onChange={(e) => set("campaigns_done", e.target.value)} data-testid="perf-campaigns" /></div>
            <div><Label className="text-xs">Review produk baru</Label><Input type="number" className="mt-1 h-9" value={f.reviews_new} onChange={(e) => set("reviews_new", e.target.value)} data-testid="perf-reviews" /></div>
            <div><Label className="text-xs">Referral aktif</Label><Input type="number" className="mt-1 h-9" value={f.referrals_active} onChange={(e) => set("referrals_active", e.target.value)} data-testid="perf-referrals" /></div>
          </>)}
          <div className="sm:col-span-2"><Label className="text-xs">Bukti / catatan</Label><Input className="mt-1 h-9" value={f.evidence} onChange={(e) => set("evidence", e.target.value)} data-testid="perf-evidence" /></div>
        </div>
        <Button onClick={save} className="mt-4 rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="perf-save">Simpan & Rekonsiliasi Point</Button>
      </div>

      <p className="text-sm font-medium text-showty-navy mb-2">Riwayat input performa</p>
      <div className="rounded-2xl border border-border bg-white overflow-x-auto" data-testid="perf-list">
        <table className="w-full text-sm">
          <thead className="bg-showty-navy text-white"><tr className="text-left">
            <th className="px-3 py-2 text-xs">Cohort</th><th className="px-3 py-2 text-xs">Jalur</th><th className="px-3 py-2 text-xs">GMV</th>
            <th className="px-3 py-2 text-xs">Order</th><th className="px-3 py-2 text-xs">Verified</th><th className="px-3 py-2 text-xs">Update</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Belum ada data</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border even:bg-showty-cream/40">
                <td className="px-3 py-2">{r.cohort}</td><td className="px-3 py-2 uppercase text-xs">{r.path}</td>
                <td className="px-3 py-2">{nf(r.gmv_net)}</td><td className="px-3 py-2">{r.orders_valid}</td>
                <td className="px-3 py-2">{r.verified ? "✅" : "—"}</td><td className="px-3 py-2 text-xs text-muted-foreground">{r.updated_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------------- Rewards ---------------- */
const RewardsTab = () => {
  const [items, setItems] = useState([]);
  const [reds, setReds] = useState([]);
  const load = useCallback(async () => {
    const [r, rd] = await Promise.all([api.get("/points/rewards"), api.get("/points/redemptions/all")]);
    setItems(r.data); setReds(rd.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const remove = async (id) => { try { await api.delete(`/points/rewards/${id}`); load(); } catch { toast.error("Gagal hapus"); } };
  const fulfill = async (id, status) => { try { await api.post(`/points/redemptions/${id}/fulfill`, { status }); toast.success(status === "cancelled" ? "Dibatalkan & point dikembalikan" : "Diperbarui"); load(); } catch { toast.error("Gagal"); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="font-medium text-showty-navy">Katalog Reward</p>
        <RewardDialog onDone={load} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="admin-rewards">
        {items.map((r) => (
          <div key={r.id} className="rounded-xl border border-border bg-white p-4" data-testid={`admin-reward-${r.id}`}>
            <div className="flex items-start justify-between">
              <div><h4 className="font-display text-lg text-showty-navy">{r.name}</h4><p className="text-xs text-muted-foreground">{r.description}</p></div>
              <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-showty-red font-semibold">{nf(r.cost)} pt</span>
              <span className="text-xs text-muted-foreground">Stok {r.stock} · {r.active ? "aktif" : "nonaktif"}</span>
            </div>
          </div>
        ))}
      </div>
      <p className="font-medium text-showty-navy mb-2">Penukaran Member</p>
      <div className="rounded-2xl border border-border bg-white overflow-x-auto" data-testid="admin-redemptions">
        <table className="w-full text-sm">
          <thead className="bg-showty-navy text-white"><tr className="text-left">
            <th className="px-3 py-2 text-xs">Member</th><th className="px-3 py-2 text-xs">Reward</th><th className="px-3 py-2 text-xs">Point</th>
            <th className="px-3 py-2 text-xs">Status</th><th className="px-3 py-2 text-xs">Aksi</th>
          </tr></thead>
          <tbody>
            {reds.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Belum ada penukaran</td></tr>}
            {reds.map((r) => (
              <tr key={r.id} className="border-t border-border even:bg-showty-cream/40" data-testid={`admin-red-${r.id}`}>
                <td className="px-3 py-2">{r.user_name || r.user_email}</td><td className="px-3 py-2">{r.reward_name}</td>
                <td className="px-3 py-2 text-showty-red">-{nf(r.cost)}</td>
                <td className="px-3 py-2"><span className="text-[11px] rounded-full bg-showty-pink/30 px-2 py-0.5">{r.status}</span></td>
                <td className="px-3 py-2">
                  {r.status !== "cancelled" && <div className="flex gap-1">
                    {r.status !== "fulfilled" && <Button size="sm" onClick={() => fulfill(r.id, "fulfilled")} className="h-7 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`red-fulfill-${r.id}`}>Fulfill</Button>}
                    <Button size="sm" variant="outline" onClick={() => fulfill(r.id, "cancelled")} className="h-7 rounded-full text-xs border-destructive text-destructive" data-testid={`red-cancel-${r.id}`}>Batal & Refund</Button>
                  </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RewardDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const empty = { name: "", cost: 500, description: "", image_url: "", stock: 100, active: true, limit_per_user: 0, terms: "", linked_sample: false };
  const [f, setF] = useState(empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Nama reward wajib");
    try { await api.post("/points/rewards", { ...f, cost: Number(f.cost) || 0, stock: Number(f.stock) || 0, limit_per_user: Number(f.limit_per_user) || 0 }); toast.success("Reward ditambahkan"); setOpen(false); setF(empty); onDone?.(); }
    catch { toast.error("Gagal simpan"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="reward-add-btn"><Plus className="w-4 h-4 mr-1" /> Reward</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="reward-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">Tambah Reward</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Nama</Label><Input className="mt-1" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="reward-name" /></div>
          <div><Label>Deskripsi</Label><Textarea className="mt-1" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="reward-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Harga (point)</Label><Input type="number" className="mt-1" value={f.cost} onChange={(e) => set("cost", e.target.value)} data-testid="reward-cost" /></div>
            <div><Label>Stok</Label><Input type="number" className="mt-1" value={f.stock} onChange={(e) => set("stock", e.target.value)} data-testid="reward-stock" /></div>
            <div><Label>Batas/user (0=bebas)</Label><Input type="number" className="mt-1" value={f.limit_per_user} onChange={(e) => set("limit_per_user", e.target.value)} data-testid="reward-limit" /></div>
            <div><Label>URL Gambar</Label><Input className="mt-1" value={f.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="reward-image" /></div>
          </div>
          <div><Label>Syarat / fulfillment</Label><Input className="mt-1" value={f.terms} onChange={(e) => set("terms", e.target.value)} data-testid="reward-terms" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="reward-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Audit / Adjust ---------------- */
const AuditTab = () => {
  const [members, setMembers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [channels, setChannels] = useState([]);
  const empty = { user_id: "", path: "kol", points: 0, reason: "", kind: "adjust" };
  const [f, setF] = useState(empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = useCallback(async () => {
    const [m, a, c] = await Promise.all([api.get("/points/members"), api.get("/points/audit"), api.get("/points/channels/pending")]);
    setMembers(m.data); setAudit(a.data); setChannels(c.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const submit = async () => {
    if (!f.user_id || !f.reason.trim()) return toast.error("Member & alasan wajib");
    try { await api.post("/points/adjust", { ...f, points: Number(f.points) || 0 }); toast.success("Penyesuaian tersimpan"); setF(empty); load(); }
    catch { toast.error("Gagal"); }
  };
  const verify = async (id) => { try { await api.post(`/points/channels/${id}/verify`); toast.success("Channel diverifikasi, +point diberikan"); load(); } catch { toast.error("Gagal"); } };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="adjust-form">
        <p className="font-medium text-showty-navy mb-3">Penyesuaian / Penalti Manual (wajib beralasan, tercatat di audit)</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2"><Label className="text-xs">Member</Label>
            <Select value={f.user_id} onValueChange={(v) => set("user_id", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="adjust-member"><SelectValue placeholder="Pilih member" /></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Jalur</Label>
            <Select value={f.path} onValueChange={(v) => set("path", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="adjust-path"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="kol">KOL</SelectItem><SelectItem value="aff">Affiliator</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Jenis</Label>
            <Select value={f.kind} onValueChange={(v) => set("kind", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="adjust-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="adjust">Koreksi (+/-)</SelectItem><SelectItem value="penalty">Penalti</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Point (+/-)</Label><Input type="number" className="mt-1 h-9" value={f.points} onChange={(e) => set("points", e.target.value)} data-testid="adjust-points" /></div>
          <div className="sm:col-span-2 lg:col-span-4"><Label className="text-xs">Alasan</Label><Input className="mt-1 h-9" value={f.reason} onChange={(e) => set("reason", e.target.value)} data-testid="adjust-reason" /></div>
          <div className="flex items-end"><Button onClick={submit} className="rounded-full bg-showty-navy hover:bg-showty-navy/90 text-white h-9 w-full" data-testid="adjust-save">Terapkan</Button></div>
        </div>
      </div>

      {channels.length > 0 && (
        <div className="rounded-2xl border border-border bg-white p-5" data-testid="channels-pending">
          <p className="font-medium text-showty-navy mb-3">Channel menunggu verifikasi</p>
          <div className="space-y-2">
            {channels.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                <span className="text-showty-navy">{c.user_name} · <span className="capitalize">{c.platform}</span> {c.handle}</span>
                <Button size="sm" onClick={() => verify(c.id)} className="h-7 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`verify-channel-${c.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Verifikasi</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="font-medium text-showty-navy mb-2">Riwayat Audit</p>
        <div className="rounded-2xl border border-border bg-white overflow-x-auto" data-testid="audit-list">
          <table className="w-full text-sm">
            <thead className="bg-showty-navy text-white"><tr className="text-left">
              <th className="px-3 py-2 text-xs">Waktu</th><th className="px-3 py-2 text-xs">Aksi</th><th className="px-3 py-2 text-xs">Admin</th><th className="px-3 py-2 text-xs">Alasan</th>
            </tr></thead>
            <tbody>
              {audit.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Belum ada audit</td></tr>}
              {audit.map((a) => (
                <tr key={a.id} className="border-t border-border even:bg-showty-cream/40">
                  <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString("id-ID")}</td>
                  <td className="px-3 py-2">{a.action}</td><td className="px-3 py-2 text-xs">{a.actor}</td><td className="px-3 py-2 text-xs">{a.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Config ---------------- */
const ConfigTab = () => {
  const [cfg, setCfg] = useState(null);
  const [kol, setKol] = useState({});
  const [aff, setAff] = useState({});
  const load = useCallback(() => api.get("/points/config").then((r) => { setCfg(r.data); setKol(r.data.kol_rules); setAff(r.data.aff_rules); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.put("/points/config", { kol_rules: numify(kol), aff_rules: numify(aff) }); toast.success("Konfigurasi disimpan"); load(); }
    catch { toast.error("Gagal simpan"); }
  };
  if (!cfg) return null;
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">Ubah nilai point per aktivitas. Milestone menggunakan nilai tertinggi yang tercapai; perubahan hanya berpengaruh ke perhitungan berikutnya.</p>
      <div className="grid lg:grid-cols-2 gap-5">
        <RuleEditor title="Aturan KOL" rules={kol} onChange={setKol} testid="cfg-kol" />
        <RuleEditor title="Aturan Affiliator" rules={aff} onChange={setAff} testid="cfg-aff" />
      </div>
      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <TierView title="Tier KOL" tiers={cfg.kol_tiers} />
        <TierView title="Tier Affiliator" tiers={cfg.aff_tiers} />
      </div>
      <Button onClick={save} className="mt-5 rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="cfg-save">Simpan Konfigurasi</Button>
    </div>
  );
};
const numify = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, Number(v) || 0]));
const RuleEditor = ({ title, rules, onChange, testid }) => (
  <div className="rounded-2xl border border-border bg-white p-4" data-testid={testid}>
    <p className="font-medium text-showty-navy mb-3">{title}</p>
    <div className="grid grid-cols-2 gap-2">
      {Object.entries(rules).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground flex-1 truncate">{k}</span>
          <Input type="number" className="h-8 w-20" value={v} onChange={(e) => onChange({ ...rules, [k]: e.target.value })} data-testid={`${testid}-${k}`} />
        </div>
      ))}
    </div>
  </div>
);
const TierView = ({ title, tiers }) => (
  <div className="rounded-2xl border border-border bg-white p-4">
    <p className="font-medium text-showty-navy mb-2">{title}</p>
    <div className="space-y-1">
      {tiers.map((t) => (
        <div key={t.name} className="flex items-center justify-between text-sm border-b border-border/50 py-1 last:border-0">
          <span className="text-showty-navy">{t.name}</span>
          <span className="text-xs text-muted-foreground">{nf(t.min)}{t.max != null ? ` – ${nf(t.max)}` : "+"}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ---------------- Backfill ---------------- */
const BackfillTab = () => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPreview = async () => { setBusy(true); try { const r = await api.post("/points/backfill/preview"); setPreview(r.data); } catch { toast.error("Gagal"); } setBusy(false); };
  const doCommit = async () => { setBusy(true); try { const r = await api.post("/points/backfill/commit"); toast.success(`Backfill selesai: +${nf(r.data.total_points)} point`); setPreview(r.data); } catch { toast.error("Gagal"); } setBusy(false); };
  return (
    <div>
      <div className="rounded-xl bg-showty-cream border border-showty-gold/30 px-4 py-2.5 mb-4 text-xs text-showty-navy">
        Backfill memberikan point <b>welcome + konten approved</b> untuk member existing (idempoten). Preview dulu sebelum commit.
      </div>
      <div className="flex gap-2 mb-4">
        <Button onClick={doPreview} disabled={busy} variant="outline" className="rounded-full" data-testid="backfill-preview"><RotateCcw className="w-4 h-4 mr-1" /> Preview</Button>
        <Button onClick={doCommit} disabled={busy || !preview} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="backfill-commit"><CheckCircle2 className="w-4 h-4 mr-1" /> Konfirmasi & Commit</Button>
      </div>
      {preview && (
        <div data-testid="backfill-result">
          <p className="text-sm text-showty-navy mb-2">{preview.committed ? "✅ Sudah di-commit" : "Preview"} — Total {nf(preview.total_points)} point untuk {preview.members?.length || 0} member</p>
          <div className="rounded-2xl border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-showty-navy text-white"><tr className="text-left">
                <th className="px-3 py-2 text-xs">Member</th><th className="px-3 py-2 text-xs">Jalur</th><th className="px-3 py-2 text-xs">Aktivitas</th><th className="px-3 py-2 text-xs">Point</th>
              </tr></thead>
              <tbody>
                {preview.members?.map((m, i) => (
                  <tr key={i} className="border-t border-border even:bg-showty-cream/40">
                    <td className="px-3 py-2">{m.name || m.email}</td><td className="px-3 py-2 uppercase text-xs">{m.path}</td>
                    <td className="px-3 py-2">{m.activities}</td><td className="px-3 py-2 text-green-600 font-semibold">+{nf(m.points)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
